import {useEffect, useMemo, useRef, useState, type FocusEvent, type FormEvent} from 'react'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {HelpIcon} from '@/components/help-icon'
import {Textarea} from '@/components/ui/textarea'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Label} from '@/components/ui/label'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {FieldError} from '@/components/field-error'
import {KairosMark} from '@/components/kairos-mark'
import {sgsstPageGuard} from '@/components/no-access-state'
import {PageNotices} from '@/components/page-notices'
import {useRegimeDriftSeverity} from '@/components/regime-drift-banner'
import {
  avisoSinSstDeLaFila,
  avisosDeLaEvaluacion,
  filaHospedaElSinSst,
} from '@/components/sgsst/avisos-de-la-evaluacion'
import {ActionFormDialog} from '@/components/action-form-dialog'
import {PlanPropuestaInterstitial} from '@/components/plan-propuesta-interstitial'
import {MicroLabel} from '@/components/period-selector'
import {ReadinessStrip, type AuditFilter} from '@/components/readiness-strip'
import {CYCLE_ORDER, GroupByToggle, readGroupBy, type GroupBy} from '@/components/sgsst/group-by-toggle'
import {MissingRiskState, SetupCard} from '@/components/sgsst/setup-card'
import {StickyProgressStrip} from '@/components/sgsst/sticky-progress-strip'
import {CierreTab} from '@/components/sgsst/cierre-tab'
import {ArtefactosPanel} from '@/components/sgsst/artefactos-panel'
import {IPEVR_41X_CODES, Ipevr41xGroupHelp, ItemRow, MARK_PRESSED} from '@/components/sgsst/item-row'
import {ChecklistGroupCard, resumenDelGrupo} from '@/components/sgsst/checklist-group-card'
import {useStuckOnScroll} from '@/components/sgsst/use-stuck-on-scroll'
import {useIsSystemUser} from '@/hooks/empresa/organizacion/use-businesses'
import {useAllEmployees} from '@/hooks/empresa/identidad/use-employees'
import {useEvidence} from '@/hooks/sgsst/evidencias/use-evidence'
import {useSolicitudes, type Solicitud} from '@/hooks/sgsst/evidencias/use-solicitudes'
import {useActions} from '@/hooks/sgsst/plan/use-actions'
import {isExpired, toSoporteDesignations, useDesignations} from '@/hooks/sgsst/evaluacion/use-designations'
import {
  useCorrectItem,
  useMarkItem,
  useSgsstPage,
  useUnmarkItem,
  type SgsstItem,
  type SgsstItemStatus,
} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {SGSST_GROUP_BY_KEY} from '@/lib/constants'
import {formatDate, formatRatePct} from '@/lib/risk'
import {groupEvidenceByStandard, readiness, soporte} from '@/lib/sgsst-readiness'
import {track} from '@/lib/telemetry'
import {translateApiError} from '@/lib/errors'
import {validateRequired} from '@/lib/validation'
import {cn} from '@/lib/utils'
import {tope} from '@/lib/campos-texto'

const SGSST_TABS = ['evaluacion', 'artefactos', 'cierre'] as const
type SgsstTab = (typeof SGSST_TABS)[number]

export function SgsstPage() {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const {
    businessId,
    hash,
    searchParams,
    viewedYear,
    business,
    isOwner,
    canOperate,
    canFirmar,
    firmaBlockedReason,
    canDisenar,
    disenoBlockedReason,
    certDevOverride,
    tieneSstVerificado,
    tieneResponsableDesignado,
    state,
    godReadOnly,
    loading,
  } = useSgsstPage()
  const revealCode = hash.startsWith('#std-') ? hash.slice('#std-'.length) : null
  const tabParam = searchParams.get('tab')
  const tab: SgsstTab = SGSST_TABS.includes(tabParam as SgsstTab) ? (tabParam as SgsstTab) : 'evaluacion'
  function setTab(next: SgsstTab) {
    // `setSearchParams` reescribe la URL entera y se lleva el fragmento: el hash se repone a mano para
    // que un deep-link `#std-…` revele su fila.
    if (next === tab) return
    const params = new URLSearchParams(searchParams)
    params.set('tab', next)
    navigate({search: `?${params.toString()}`, hash}, {replace: true})
  }
  // La pestaña obedece al hash cada vez que cambia: «Ir a la fila →» de la vitrina solo cambia el fragmento.
  useEffect(() => {
    if (hash === '#artefactos') setTab('artefactos')
    else if (hash.startsWith('#std-')) setTab('evaluacion')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash])
  const {sentinelRef, stuck} = useStuckOnScroll()
  // Un año cerrado es un registro legal congelado: solo lectura para todos.
  const editable = canOperate && state?.assessment?.is_closed === false && !godReadOnly
  // Atestar es de dueño o responsable por rol y sin licencia (el art. 27 pide dos firmas), nunca modo dios
  // virtual (identity-access-spec §1a.3). `certDevOverride` solo existe en dev para E2E.
  const canAttest = canFirmar || certDevOverride
  const canConfirmSugerido = canDisenar || certDevOverride
  const isSystemUser = useIsSystemUser()
  const fase2 = !!state?.assessment?.autoevaluacion_completed_at
  const driftSeverity = useRegimeDriftSeverity(businessId!, fase2)
  // Denominador de abandono: una vez por evaluación; el numerador es `autoevaluacion_completada`.
  const iniciadaRef = useRef<string | null>(null)
  useEffect(() => {
    const assessmentId = state?.assessment?.id
    if (!assessmentId || fase2) return
    if (iniciadaRef.current === assessmentId) return
    iniciadaRef.current = assessmentId
    track('autoevaluacion_iniciada', {chapter: state!.assessment!.chapter})
  }, [state, fase2])
  const {data: yearEvidence} = useEvidence(canOperate && state?.assessment ? businessId : undefined, viewedYear)
  // Hito del embudo: una vez al pasar de 0 a ≥1 soportes; la clave empresa+año evita falsos disparos.
  const firstSoporteRef = useRef<{key: string; count: number} | null>(null)
  useEffect(() => {
    if (!businessId) return
    const count = yearEvidence?.items.length
    if (count === undefined) return
    const key = `${businessId}:${viewedYear ?? 'current'}`
    const prev = firstSoporteRef.current
    if (prev && prev.key === key && prev.count === 0 && count > 0) {
      track('first_soporte_uploaded', {business_id: businessId})
    }
    firstSoporteRef.current = {key, count}
  }, [businessId, viewedYear, yearEvidence])
  const {data: yearSolicitudes} = useSolicitudes(canOperate && state?.assessment ? businessId : undefined, viewedYear)
  const {data: actionsPage} = useActions(canOperate && state?.assessment ? businessId : undefined, viewedYear)
  const {data: yearDesignations} = useDesignations(canOperate && state?.assessment ? businessId : undefined, viewedYear)
  const {data: roster} = useAllEmployees(canOperate ? businessId : undefined)
  const markItem = useMarkItem(businessId!)
  const unmarkItem = useUnmarkItem(businessId!)
  const correctItem = useCorrectItem(businessId!)
  // Un solo filtro a la vez; la línea de Resumen enlaza con `?filter=` para llegar con un chip puesto.
  const chipParam = searchParams.get('filter')
  const [filter, setFilter] = useState<'pending' | AuditFilter | null>(
    chipParam === 'sin-soporte' || chipParam === 'vigencia' ? chipParam : null
  )
  const [groupBy, setGroupBy] = useState<GroupBy>(readGroupBy)
  function chooseGroupBy(next: GroupBy) {
    setGroupBy(next)
    localStorage.setItem(SGSST_GROUP_BY_KEY, next)
  }
  const [justifying, setJustifying] = useState<SgsstItem | null>(null)
  const [justification, setJustification] = useState('')
  const [justificationError, setJustificationError] = useState<string | undefined>()
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [correcting, setCorrecting] = useState<SgsstItem | null>(null)
  const [corrStatus, setCorrStatus] = useState<SgsstItemStatus>('cumple')
  const [corrNote, setCorrNote] = useState('')
  const [corrNoteError, setCorrNoteError] = useState<string | undefined>()
  const [corrJustification, setCorrJustification] = useState('')
  const [corrJustificationError, setCorrJustificationError] = useState<string | undefined>()
  const [addingFor, setAddingFor] = useState<SgsstItem | null>(null)
  const [addingSuggestedTitulo, setAddingSuggestedTitulo] = useState<string | null>(null)
  // Vive en la página y no en `TerminarControls`: terminar pone fase2 en true y desmonta ambas
  // instancias antes de que el diálogo alcance a pintarse.
  const [offering, setOffering] = useState(false)

  // Una designación vigente con carta es soporte como un archivo; sin carta nunca cuenta (1.1.1).
  const soporteDesignations = useMemo(() => toSoporteDesignations(yearDesignations?.items ?? []), [yearDesignations])
  const standardsWithVigenteDesignation = useMemo(() => {
    const set = new Set<string>()
    for (const d of yearDesignations?.items ?? []) {
      if (d.archived_at === null && !isExpired(d)) set.add(d.standard_code)
    }
    return set
  }, [yearDesignations])
  const audit = useMemo(
    () => readiness(state?.assessment?.items ?? [], yearEvidence?.items ?? [], soporteDesignations),
    [state, yearEvidence, soporteDesignations]
  )
  // Un chip que llega a cero deja de filtrar en vez de dejar la lista vacía.
  const effectiveFilter =
    (filter === 'sin-soporte' && audit.sinSoporte.length === 0) ||
    (filter === 'vigencia' && audit.pendingVigencia.length === 0)
      ? null
      : filter

  const groups = useMemo(() => {
    const items = state?.assessment?.items ?? []
    const visible =
      effectiveFilter === 'pending'
        ? items.filter((item) => !item.status)
        : effectiveFilter === 'sin-soporte'
          ? items.filter((item) => audit.sinSoporte.includes(item.code))
          : effectiveFilter === 'vigencia'
            ? items.filter((item) => audit.pendingVigencia.includes(item.code))
            : items
    const sections: {cycle: string; groupName: string; items: SgsstItem[]}[] = []
    const byGroup = new Map<string, SgsstItem[]>()
    for (const item of visible) {
      let bucket = byGroup.get(item.group_name)
      if (!bucket) {
        bucket = []
        byGroup.set(item.group_name, bucket)
        sections.push({cycle: item.cycle, groupName: item.group_name, items: bucket})
      }
      bucket.push(item)
    }
    return sections
  }, [state, effectiveFilter, audit])

  const actionCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const action of actionsPage?.items ?? []) {
      if (!action.standard_code) continue
      counts.set(action.standard_code, (counts.get(action.standard_code) ?? 0) + 1)
    }
    return counts
  }, [actionsPage])

  function handleMark(item: SgsstItem, status: SgsstItemStatus) {
    if (status === 'no_aplica' && !item.status) {
      setJustifying(item)
      setJustification(item.justification ?? '')
      setJustificationError(undefined)
      setDialogError(null)
      return
    }
    if (item.status && item.status !== status) {
      // Devolver una marca existente a otro estado exige nota, nunca sobrescribir en silencio (marcado §4.1).
      setCorrecting(item)
      setCorrStatus(status)
      setCorrNote('')
      setCorrNoteError(undefined)
      setCorrJustification(item.justification ?? '')
      setCorrJustificationError(undefined)
      setDialogError(null)
      return
    }
    markItem.mutate({
      code: item.code,
      status,
      justification: status === 'no_aplica' ? (item.justification ?? null) : null,
    })
  }

  function handleUndo(item: SgsstItem) {
    unmarkItem.mutate(item.code)
  }

  function handleCorrect(item: SgsstItem) {
    setCorrecting(item)
    setCorrStatus(item.status ?? 'cumple')
    setCorrNote('')
    setCorrNoteError(undefined)
    setCorrJustification(item.justification ?? '')
    setCorrJustificationError(undefined)
    setDialogError(null)
  }

  function blurJustification(e: FocusEvent<HTMLTextAreaElement>) {
    setJustificationError(validateRequired(e.target.value) ?? undefined)
  }

  function submitJustification(e: FormEvent) {
    e.preventDefault()
    const err = validateRequired(justification)
    setJustificationError(err ?? undefined)
    if (err) return
    markItem.mutate(
      {code: justifying!.code, status: 'no_aplica', justification},
      {
        onSuccess: () => setJustifying(null),
        onError: (err) => setDialogError(translateApiError(err)),
      }
    )
  }

  function blurCorrNote(e: FocusEvent<HTMLTextAreaElement>) {
    setCorrNoteError(validateRequired(e.target.value) ?? undefined)
  }

  function blurCorrJustification(e: FocusEvent<HTMLTextAreaElement>) {
    setCorrJustificationError(validateRequired(e.target.value) ?? undefined)
  }

  function submitCorrection(e: FormEvent) {
    e.preventDefault()
    const noteErr = validateRequired(corrNote)
    const justificationErr = corrStatus === 'no_aplica' ? validateRequired(corrJustification) : null
    setCorrNoteError(noteErr ?? undefined)
    setCorrJustificationError(justificationErr ?? undefined)
    if (noteErr || justificationErr) return
    const justification = corrStatus === 'no_aplica' ? corrJustification : null
    const onSettled = {
      onSuccess: () => setCorrecting(null),
      onError: (err: unknown) => setDialogError(translateApiError(err)),
    }
    if (fase2)
      correctItem.mutate({code: correcting!.code, status: corrStatus, note: corrNote, justification}, onSettled)
    else markItem.mutate({code: correcting!.code, status: corrStatus, note: corrNote, justification}, onSettled)
  }

  const guard = sgsstPageGuard(loading, !!business && canOperate, state)
  if (guard !== null || !state) return guard

  const allItems = state.assessment?.items ?? []
  const evidenceByStandard = groupEvidenceByStandard(yearEvidence?.items ?? [])
  const solicitudesByStandard = new Map<string, Solicitud[]>()
  for (const solicitud of yearSolicitudes ?? []) {
    if (!solicitudesByStandard.has(solicitud.standard_code)) solicitudesByStandard.set(solicitud.standard_code, [])
    solicitudesByStandard.get(solicitud.standard_code)!.push(solicitud)
  }
  const assessment = state.assessment
  const planHref = assessment
    ? `/businesses/${businessId}/sgsst/plan?year=${assessment.period_year}`
    : `/businesses/${businessId}/sgsst/plan`

  const avisosDePagina = avisosDeLaEvaluacion({
    businessId: businessId!,
    state,
    driftSeverity,
    fase2,
    editable: !!editable,
    isOwner,
    godReadOnly,
    isSystemUser,
    tieneSstVerificado,
    tieneResponsableDesignado,
    canDisenar,
    disenoBlockedReason,
    filaHospeda: filaHospedaElSinSst(tab, groups),
  })

  function needsDesignation(item: SgsstItem) {
    return !!editable && item.designation_scoped && !standardsWithVigenteDesignation.has(item.code)
  }

  function renderGroupCard(groupName: string, items: SgsstItem[]) {
    const groupAll = allItems.filter((item) => item.group_name === groupName)
    const respaldaIpevr = groupAll.some((item) => IPEVR_41X_CODES.has(item.code))
    return (
      <ChecklistGroupCard
        key={groupName}
        groupName={groupName}
        {...resumenDelGrupo(groupAll)}
        ayuda={respaldaIpevr ? <Ipevr41xGroupHelp /> : undefined}>
        {items.map((item) => (
          <ItemRow
            key={item.code}
            item={item}
            fase2={fase2}
            canEdit={!!editable}
            canAttest={canAttest}
            firmaBlockedReason={firmaBlockedReason}
            canConfirmSugerido={canConfirmSugerido}
            disenoBlockedReason={disenoBlockedReason}
            onMark={handleMark}
            onUndo={handleUndo}
            onCorrect={handleCorrect}
            onAddAction={(target, titulo) => {
              setAddingFor(target)
              setAddingSuggestedTitulo(titulo ?? null)
            }}
            actionCount={actionCounts.get(item.code) ?? 0}
            planHref={`${planHref}#plan-${item.code}`}
            businessId={businessId!}
            evidence={evidenceByStandard.get(item.code) ?? []}
            solicitudes={solicitudesByStandard.get(item.code) ?? []}
            roster={roster ?? []}
            autoReveal={item.code === revealCode}
            needsDesignation={needsDesignation(item)}
            sinSst={avisoSinSstDeLaFila({
              item,
              businessId: businessId!,
              state: state!,
              puedeAsignar: isOwner && !godReadOnly,
              tieneSstVerificado,
              tieneResponsableDesignado,
              esperaDesignacion: needsDesignation(item),
              roster: roster ?? [],
            })}
            sinSoporte={item.status === 'cumple' && !soporte(item, evidenceByStandard, soporteDesignations)}
            coverage={{
              year: assessment!.period_year,
              isClosed: assessment!.is_closed,
              workerCount: assessment!.worker_count,
            }}
          />
        ))}
      </ChecklistGroupCard>
    )
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 text-foreground">
        <KairosMark />
        <h1 className="font-heading text-xl">{t('sgsst.title')}</h1>
      </div>

      {/* Ningún aviso se auto-oculta: es la familia que la ley exige ver. */}
      <PageNotices items={avisosDePagina} />

      {state.risk_class === null && !assessment ? (
        <MissingRiskState businessId={businessId!} />
      ) : !assessment ? (
        // canInvite excluye al responsable (§1a.3); el modo dios de solo lectura igual abre el diálogo.
        <SetupCard businessId={businessId!} state={state} godReadOnly={godReadOnly} canInvite={isOwner} />
      ) : (
        <>
          {stuck && tab === 'evaluacion' && (
            <StickyProgressStrip stuck={stuck} assessment={assessment} fase2={fase2} planHref={planHref} />
          )}

          <Tabs value={tab} onValueChange={(v) => setTab(v as SgsstTab)}>
            <TabsList data-testid="sgsst-view-tabs" aria-label={t('sgsst.tabs.label')}>
              <TabsTrigger value="evaluacion" data-testid="sgsst-view-tab-evaluacion">
                {t('sgsst.tabs.evaluacion')}
              </TabsTrigger>
              <TabsTrigger value="artefactos" data-testid="sgsst-view-tab-artefactos">
                {t('sgsst.tabs.artefactos')}
              </TabsTrigger>
              <TabsTrigger value="cierre" data-testid="sgsst-view-tab-cierre">
                {t('sgsst.tabs.cierre')}
              </TabsTrigger>
            </TabsList>
            {/* El centinela mide la cabecera, no la lista: envolviendo las tabs nunca dejaba de intersecar. */}
            <div ref={sentinelRef} aria-hidden className="h-0" />

            <TabsContent value="evaluacion" className="space-y-5 pt-4">
              <div className="surface overflow-hidden">
                <ReadinessStrip
                  audit={audit}
                  isClosed={assessment.is_closed}
                  filter={effectiveFilter}
                  onToggle={(chip) => setFilter(filter === chip ? null : chip)}
                />
                <div
                  data-testid="checklist-toolbar"
                  className="surface-divider flex flex-wrap items-center gap-2 border-t border-dashed px-4 py-2.5">
                  <GroupByToggle value={groupBy} onChange={chooseGroupBy} />
                  <button
                    type="button"
                    aria-pressed={effectiveFilter === 'pending'}
                    onClick={() => setFilter(filter === 'pending' ? null : 'pending')}
                    className={cn(
                      'rounded-full border px-3 py-1 font-mono text-[11px] tracking-wide',
                      effectiveFilter === 'pending'
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-input text-muted-foreground hover:text-foreground'
                    )}>
                    {t('sgsst.pendingOnly')}
                  </button>
                  {!fase2 && editable && (
                    <HelpIcon label={t('sgsst.controls.helpLabel')} testId="sgsst-controls-help">
                      {t('sgsst.controls.help')}
                    </HelpIcon>
                  )}
                </div>
              </div>

              {groupBy === 'phase' ? (
                // VERIFICAR no tiene estándares en la Tabla de Valores, así que CYCLE_ORDER lo omite.
                <div data-testid="checklist-por-ciclo">
                  {CYCLE_ORDER.filter((cycle) => groups.some((s) => s.cycle === cycle)).map((cycle) => (
                    <section key={cycle} className="mt-5 first:mt-0">
                      <MicroLabel className="mb-2">{cycle}</MicroLabel>
                      <div className="space-y-3">
                        {groups.filter((s) => s.cycle === cycle).map((s) => renderGroupCard(s.groupName, s.items))}
                      </div>
                    </section>
                  ))}
                </div>
              ) : (
                <div className="space-y-3" data-testid="checklist-por-numeral">
                  {groups.map((s) => renderGroupCard(s.groupName, s.items))}
                </div>
              )}
              {effectiveFilter === 'pending' && allItems.every((item) => item.status) && (
                <p className="text-sm text-muted-foreground">{t('sgsst.noPending')}</p>
              )}
            </TabsContent>

            <TabsContent value="artefactos" className="pt-4">
              <ArtefactosPanel businessId={businessId!} allItems={allItems} enabled={canOperate} />
            </TabsContent>

            <TabsContent value="cierre" className="pt-4">
              <CierreTab
                state={state}
                assessment={assessment}
                businessId={businessId!}
                fase2={fase2}
                editable={!!editable}
                godReadOnly={godReadOnly}
                canAttest={canAttest}
                firmaBlockedReason={firmaBlockedReason}
                planHref={planHref}
                onFinished={() => setOffering(true)}
              />
            </TabsContent>
          </Tabs>
        </>
      )}

      <Dialog open={!!justifying} onOpenChange={(open) => !open && setJustifying(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('sgsst.justify.title', {code: justifying?.code})}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitJustification} className="space-y-4" noValidate>
            <p className="text-sm text-muted-foreground">{t('sgsst.justify.body')}</p>
            <div className="space-y-2">
              <Label htmlFor="justification">{t('sgsst.justify.label')}</Label>
              <Textarea
                maxLength={tope('NOTA')}
                id="justification"
                value={justification}
                onChange={(e) => {
                  setJustification(e.target.value)
                  setJustificationError(undefined)
                }}
                onBlur={blurJustification}
                placeholder={t('sgsst.justify.placeholder')}
                required
                minRows={3}
                aria-invalid={!!justificationError}
              />
              <FieldError error={justificationError} />
            </div>
            <FormDialogFooter
              error={dialogError}
              pending={markItem.isPending}
              submitLabel={t('sgsst.justify.submit')}
              onCancel={() => setJustifying(null)}
            />
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!correcting} onOpenChange={(open) => !open && setCorrecting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('sgsst.correction.title', {code: correcting?.code})}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitCorrection} className="space-y-4" data-testid="correction-form" noValidate>
            <p className="text-sm text-muted-foreground">
              {assessment?.autoevaluacion_completed_at
                ? t('sgsst.correction.body', {
                    date: formatDate(assessment.autoevaluacion_completed_at),
                    pct: formatRatePct(assessment.snapshot_pct ?? 0),
                  })
                : t('marcado.correction.fase1Body')}
            </p>
            <div className="space-y-2">
              <Label>{t('sgsst.correction.statusLabel')}</Label>
              <div className="flex w-fit gap-0.5 rounded-lg bg-secondary p-0.5" role="group">
                {(['cumple', 'no_cumple', 'no_aplica'] as const).map((status) => (
                  <button
                    key={status}
                    type="button"
                    aria-pressed={corrStatus === status}
                    onClick={() => setCorrStatus(status)}
                    className={cn(
                      'rounded-md px-2.5 py-1.5 text-xs whitespace-nowrap transition-colors',
                      corrStatus === status
                        ? cn('font-semibold shadow-xs', MARK_PRESSED[status])
                        : 'text-muted-foreground hover:bg-card hover:text-foreground'
                    )}>
                    {t(`sgsst.controls.${status}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="correction_note">{t('sgsst.correction.noteLabel')}</Label>
              <Textarea
                maxLength={tope('NOTA')}
                id="correction_note"
                value={corrNote}
                onChange={(e) => {
                  setCorrNote(e.target.value)
                  setCorrNoteError(undefined)
                }}
                onBlur={blurCorrNote}
                required
                minRows={3}
                aria-invalid={!!corrNoteError}
              />
              <FieldError error={corrNoteError} />
            </div>
            {corrStatus === 'no_aplica' && (
              <div className="space-y-2">
                <Label htmlFor="correction_justification">{t('sgsst.correction.justificationLabel')}</Label>
                <Textarea
                  maxLength={tope('NOTA')}
                  id="correction_justification"
                  value={corrJustification}
                  onChange={(e) => {
                    setCorrJustification(e.target.value)
                    setCorrJustificationError(undefined)
                  }}
                  onBlur={blurCorrJustification}
                  required
                  minRows={2}
                  aria-invalid={!!corrJustificationError}
                />
                <FieldError error={corrJustificationError} />
              </div>
            )}
            <FormDialogFooter
              error={dialogError}
              pending={fase2 ? correctItem.isPending : markItem.isPending}
              submitLabel={t('sgsst.correction.submit')}
              onCancel={() => setCorrecting(null)}
            />
          </form>
        </DialogContent>
      </Dialog>

      <ActionFormDialog
        businessId={businessId!}
        open={!!addingFor}
        onOpenChange={(open) => {
          if (!open) {
            setAddingFor(null)
            setAddingSuggestedTitulo(null)
          }
        }}
        standardCode={addingFor?.code ?? null}
        standardName={addingFor?.name}
        roster={roster ?? []}
        evidence={yearEvidence?.items ?? []}
        initialDescription={addingSuggestedTitulo ?? undefined}
      />

      {assessment && (
        <PlanPropuestaInterstitial
          businessId={businessId!}
          year={assessment.period_year}
          open={offering}
          onDismiss={() => setOffering(false)}
        />
      )}
    </div>
  )
}
